import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IndexedDBService } from './indexeddb.service';

/**
 * Cached data structure with metadata
 */
interface CachedData<T> {
  key: string;
  data: T;
  timestamp: number;
  userId?: string;
  practiceId?: number;
  dataType?: string;
}

/**
 * Cache service options
 */
interface CacheOptions<T> {
  /** Time-to-live in milliseconds. If provided, cached data older than TTL is considered expired */
  ttl?: number;
  /** User ID prefix for cache key (for user-specific data) */
  userId?: string;
  /** Callback when cached data is returned */
  onCacheHit?: (data: T) => void;
  /** Callback when fresh data is fetched */
  onFetchComplete?: (data: T) => void;
}

/**
 * CacheService
 *
 * Provides cache-first strategy for data loading with dual-layer caching:
 * - Memory cache for fast synchronous access
 * - IndexedDB for persistent storage across browser sessions
 * - Returns cached data immediately if available
 * - Fetches fresh data in background to update cache
 * - Supports optional TTL (time-to-live) for cache expiration
 * - Supports user-scoped cache keys
 *
 * This consolidates the duplicated cache-then-fetch pattern used throughout CoreService.
 */
@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private indexedDB = inject(IndexedDBService);
  private memoryCache = new Map<string, CachedData<any>>();
  private readonly CACHE_STORE = 'metadata';
  private readonly DASHBOARD_STORE = 'dashboard_data';
  private readonly SIZE_THRESHOLD = 10 * 1024; // 10KB threshold for routing to dashboard store
  private initialized = false;

  constructor() {
    // Migrate old localStorage cache to IndexedDB
    this.migrateFromLocalStorage();
    // Initialize memory cache from IndexedDB on service creation
    this.initializeMemoryCache();
  }

  /**
   * Migrate old cache data from localStorage to IndexedDB
   * This is a one-time migration that clears old localStorage cache keys
   */
  private migrateFromLocalStorage(): void {
    const MIGRATION_FLAG = 'xmedical-cache-migrated-to-indexeddb';

    // Check if migration already completed
    if (localStorage.getItem(MIGRATION_FLAG) === 'true') {
      return;
    }

    console.log('[CacheService] Migrating cache from localStorage to IndexedDB...');

    // List of known cache keys from the old implementation
    const knownCacheKeys = [
      'patient-genders',
      'patient-titles',
      'patient-races',
      'patient-languages',
      'places-of-service',
      'master-permissions',
      'comment_types',
      'comment_templates',
      'report_templates',
      'email_templates',
      'practices',
      'practice_settings',
      'practice_procedures',
      'patient_statuses',
      'patient_flags',
      'practice_letterheads',
      'current-user-settings',
      'current-user-permissions',
      'flagged-patients',
      'todays-appointments'
    ];

    let clearedCount = 0;

    // Clear known cache keys
    for (const key of knownCacheKeys) {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        clearedCount++;
      }
    }

    // Also clear any user-prefixed versions (e.g., "userId-practices")
    // Enumerate all localStorage keys and remove cache-related ones
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Check if key matches any of our known cache patterns
        // User-prefixed keys: "userId-keyname"
        for (const cacheKey of knownCacheKeys) {
          if (key.endsWith(`-${cacheKey}`) || key === cacheKey) {
            keysToRemove.push(key);
            break;
          }
        }
      }
    }

    // Remove identified keys
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
      clearedCount++;
    }

    // Set migration flag
    localStorage.setItem(MIGRATION_FLAG, 'true');

    if (clearedCount > 0) {
      console.log(`[CacheService] Migrated cache to IndexedDB, cleared ${clearedCount} localStorage entries`);
    }
  }

  /**
   * Initialize memory cache from IndexedDB
   * Loads all cached data into memory for fast synchronous access
   */
  private async initializeMemoryCache(): Promise<void> {
    if (!IndexedDBService.isSupported()) {
      console.warn('[CacheService] IndexedDB not supported, using memory-only cache');
      this.initialized = true;
      return;
    }

    try {
      // Load from metadata store (always exists)
      const metadataCached = await this.indexedDB.getAll<CachedData<any>>(this.CACHE_STORE);

      // Try to load from dashboard_data store (might not exist during upgrade)
      let dashboardCached: CachedData<any>[] = [];
      try {
        dashboardCached = await this.indexedDB.getAll<CachedData<any>>(this.DASHBOARD_STORE);
      } catch (dashboardError) {
        // Dashboard store doesn't exist yet (database upgrade in progress)
        console.warn('[CacheService] Dashboard store unavailable, will be created on next load');
      }

      const allCached = [...metadataCached, ...dashboardCached];

      for (const cached of allCached) {
        this.memoryCache.set(cached.key, cached);
      }
      this.initialized = true;
    } catch (error) {
      console.error('[CacheService] Failed to initialize memory cache from IndexedDB', error);
      this.initialized = true; // Continue with empty memory cache
    }
  }

  /**
   * Cache-first strategy with background refresh
   *
   * @param key - Cache key
   * @param fetcher - Function that returns Observable with fresh data
   * @param options - Cache options (TTL, userId, callbacks)
   * @returns Observable that emits cached data immediately (if available) and/or fresh data
   *
   * @example
   * ```typescript
   * this.cache.cacheFirst(
   *   'user-settings',
   *   () => this.userRepo.loadSettings(),
   *   {
   *     userId: this.auth.getUserId(),
   *     ttl: 3600000, // 1 hour
   *     onCacheHit: (settings) => {
   *       this.settingsSignal.set(settings);
   *       this.events.trigger(new CoreEvent('settings_loaded'));
   *     },
   *     onFetchComplete: (settings) => {
   *       this.settingsSignal.set(settings);
   *       this.events.trigger(new CoreEvent('settings_loaded'));
   *     }
   *   }
   * );
   * ```
   */
  cacheFirst<T>(
    key: string,
    fetcher: () => Observable<T>,
    options: CacheOptions<T> = {}
  ): Observable<T> {
    const cacheKey = this.buildCacheKey(key, options.userId);
    const cached = this.get<T>(cacheKey);

    if (cached && !this.isExpired(cached, options.ttl)) {
      // Return cached data immediately
      if (options.onCacheHit) {
        // Use setTimeout to avoid executing callback synchronously
        setTimeout(() => options.onCacheHit!(cached.data), 0);
      }

      // Fetch fresh data in background to update cache
      setTimeout(() => {
        fetcher().subscribe({
          next: fresh => {
            this.set(cacheKey, fresh);
            if (options.onFetchComplete) {
              options.onFetchComplete(fresh);
            }
          },
          error: err => console.warn('[CacheService] Background refresh failed', err)
        });
      }, 0);

      return of(cached.data);
    }

    // No cache or expired, fetch from source
    return fetcher().pipe(
      tap(data => {
        this.set(cacheKey, data);
        if (options.onFetchComplete) {
          options.onFetchComplete(data);
        }
      })
    );
  }

  /**
   * Network-first strategy
   *
   * Tries to fetch from network first, falls back to cache on failure
   *
   * @param key - Cache key
   * @param fetcher - Function that returns Observable with fresh data
   * @param options - Cache options (userId)
   * @returns Observable with network or cached data
   */
  networkFirst<T>(
    key: string,
    fetcher: () => Observable<T>,
    options: Pick<CacheOptions<T>, 'userId'> = {}
  ): Observable<T> {
    const cacheKey = this.buildCacheKey(key, options.userId);

    return new Observable(observer => {
      fetcher().subscribe({
        next: data => {
          this.set(cacheKey, data);
          observer.next(data);
          observer.complete();
        },
        error: err => {
          // Network failed, try cache
          const cached = this.get<T>(cacheKey);
          if (cached) {
            console.warn('[CacheService] Network failed, using cache', err);
            observer.next(cached.data);
            observer.complete();
          } else {
            observer.error(err);
          }
        }
      });
    });
  }

  /**
   * Get cached data
   *
   * @param key - Cache key
   * @returns Cached data with metadata, or null if not found
   */
  get<T>(key: string): CachedData<T> | null {
    // Try memory cache first
    const cached = this.memoryCache.get(key);
    return cached ?? null;
  }

  /**
   * Set cached data
   *
   * Updates both memory cache and IndexedDB for persistence
   * Routes to dashboard_data store for large data, metadata store for small data
   *
   * @param key - Cache key
   * @param data - Data to cache
   */
  set<T>(key: string, data: T): void {
    // Extract metadata from key (format: userId-practiceId-dataType or just dataType)
    const metadata = this.extractMetadata(key);

    const cachedData: CachedData<T> = {
      key,
      data,
      timestamp: Date.now(),
      ...metadata
    };

    // Update memory cache immediately
    this.memoryCache.set(key, cachedData);

    // Update IndexedDB asynchronously for persistence
    if (IndexedDBService.isSupported()) {
      // Use update which will handle both add and update
      this.persistToIndexedDB(cachedData).catch(err => {
        console.error('[CacheService] Failed to persist cache to IndexedDB', err);
      });
    }
  }

  /**
   * Extract metadata from cache key
   * Handles formats: "userId-practiceId-dataType" or just "dataType"
   *
   * @param key - Cache key
   * @returns Metadata object with userId, practiceId, dataType
   */
  private extractMetadata(key: string): { userId?: string; practiceId?: number; dataType?: string } {
    const parts = key.split('-');

    // Check if key has userId-practiceId-dataType format
    if (parts.length >= 3) {
      const userId = parts[0];
      const practiceId = parseInt(parts[1], 10);
      const dataType = parts.slice(2).join('-');

      return {
        userId,
        practiceId: isNaN(practiceId) ? undefined : practiceId,
        dataType
      };
    }

    // Simple key format, just use as dataType
    return {
      dataType: key
    };
  }

  /**
   * Persist cached data to IndexedDB
   * Routes to appropriate store based on data size
   *
   * @param cachedData - Cached data to persist
   */
  private async persistToIndexedDB<T>(cachedData: CachedData<T>): Promise<void> {
    // Determine which store to use based on data size
    const dataSize = JSON.stringify(cachedData.data).length;
    const storeName = dataSize > this.SIZE_THRESHOLD ? this.DASHBOARD_STORE : this.CACHE_STORE;

    try {
      const existing = await this.indexedDB.get<CachedData<T>>(storeName, cachedData.key);
      if (existing) {
        await this.indexedDB.update(storeName, cachedData.key, cachedData);
      } else {
        await this.indexedDB.add(storeName, cachedData);
      }
    } catch (error) {
      // If add fails because key exists, try update
      await this.indexedDB.update(storeName, cachedData.key, cachedData);
    }
  }

  /**
   * Invalidate (delete) cached data
   *
   * @param key - Cache key to delete
   * @param userId - Optional user ID if key is user-scoped
   */
  invalidate(key: string, userId?: string): void {
    const cacheKey = this.buildCacheKey(key, userId);

    // Remove from memory cache
    this.memoryCache.delete(cacheKey);

    // Remove from both IndexedDB stores (we don't know which one it's in)
    if (IndexedDBService.isSupported()) {
      this.indexedDB.delete(this.CACHE_STORE, cacheKey).catch(() => {
        // Ignore errors if key not found in this store
      });
      this.indexedDB.delete(this.DASHBOARD_STORE, cacheKey).catch(() => {
        // Ignore errors if key not found in this store
      });
    }
  }

  /**
   * Invalidate all cache entries for a specific practice
   * Useful when switching practices
   *
   * @param practiceId - Practice ID to clear cache for
   */
  invalidateByPractice(practiceId: number): void {
    const keysToDelete: string[] = [];

    // Find all keys for this practice in memory cache
    for (const [key, cached] of this.memoryCache.entries()) {
      if (cached.practiceId === practiceId) {
        keysToDelete.push(key);
      }
    }

    // Delete from memory
    for (const key of keysToDelete) {
      this.memoryCache.delete(key);
    }

    // Delete from IndexedDB using index query
    if (IndexedDBService.isSupported()) {
      // Query dashboard_data by practiceId index and delete all matches
      this.indexedDB.queryByIndex<CachedData<any>>(this.DASHBOARD_STORE, 'practiceId', practiceId)
        .then(entries => {
          for (const entry of entries) {
            this.indexedDB.delete(this.DASHBOARD_STORE, entry.key).catch(err => {
              console.error(`[CacheService] Failed to delete ${entry.key}`, err);
            });
          }
        })
        .catch(err => {
          console.error('[CacheService] Failed to query by practiceId', err);
        });
    }

    // Only log if we actually invalidated something
    if (keysToDelete.length > 0) {
      console.log(`[CacheService] Cleared ${keysToDelete.length} cache entries for practice ${practiceId}`);
    }
  }

  /**
   * Invalidate all cache keys matching a pattern
   *
   * @param pattern - Regex pattern to match cache keys
   *
   * @example
   * ```typescript
   * // Clear all user-specific caches
   * cache.invalidatePattern(/^user123-/);
   *
   * // Clear all practice-related caches
   * cache.invalidatePattern(/practice/);
   * ```
   */
  invalidatePattern(pattern: RegExp): void {
    const keysToDelete: string[] = [];

    // Find matching keys in memory cache
    for (const key of this.memoryCache.keys()) {
      if (pattern.test(key)) {
        keysToDelete.push(key);
      }
    }

    // Delete from memory cache
    for (const key of keysToDelete) {
      this.memoryCache.delete(key);
    }

    // Delete from both IndexedDB stores
    if (IndexedDBService.isSupported()) {
      for (const key of keysToDelete) {
        this.indexedDB.delete(this.CACHE_STORE, key).catch(() => {
          // Ignore if not found in this store
        });
        this.indexedDB.delete(this.DASHBOARD_STORE, key).catch(() => {
          // Ignore if not found in this store
        });
      }
    }

    // Removed verbose log
  }

  /**
   * Clear all cache
   *
   * Clears both memory and IndexedDB cache (metadata and dashboard stores)
   */
  clearAll(): void {
    // Clear memory cache
    this.memoryCache.clear();

    // Clear both IndexedDB stores
    if (IndexedDBService.isSupported()) {
      this.indexedDB.clear(this.CACHE_STORE).catch(err => {
        console.error('[CacheService] Failed to clear metadata cache', err);
      });
      this.indexedDB.clear(this.DASHBOARD_STORE).catch(err => {
        console.error('[CacheService] Failed to clear dashboard cache', err);
      });
    }

    // Removed verbose log
  }

  /**
   * Build cache key with optional user prefix
   *
   * @param key - Base cache key
   * @param userId - Optional user ID for user-scoped data
   * @returns Full cache key
   */
  private buildCacheKey(key: string, userId?: string): string {
    return userId ? `${userId}-${key}` : key;
  }

  /**
   * Check if cached data is expired
   *
   * @param cached - Cached data with timestamp
   * @param ttl - Time-to-live in milliseconds
   * @returns True if expired or TTL not set
   */
  private isExpired<T>(cached: CachedData<T>, ttl?: number): boolean {
    if (!ttl) { return false; } // No TTL means never expires
    return Date.now() - cached.timestamp > ttl;
  }
}
